
import React, { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import {
    Card,
    CardBody,
    Col,
    Form,
    Input,
    Label,
    Row,
    FormFeedback,
    Spinner,
    Container,
    CardHeader,
    Button
} from "reactstrap";
import { Btn } from "../../AbstractElements";
import Breadcrumbs from "../../CommonElements/Breadcrumbs";
import { apiPost, apiGet, uploadFile } from "../../Utils"; // Assuming uploadFile handles single file upload and returns filename
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "react-phone-input-2/lib/style.css";
import PhoneInput from "react-phone-input-2";
import { parsePhoneNumberFromString, CountryCode } from "libphonenumber-js";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import * as XLSX from 'xlsx';

interface FormValues {
    name: string;
    email: string;
    phone_number: string;
    status: string;
    gender: string;
    date_of_birth: string;
    address: string;
    blood_group: string;
    nationality: string;
    license_number: string;
    license_expiry_date: string;
    joining_date: string;
    years_of_experience: string;
    assigned_branch_id: string;
    driver_image: FileList;
    license_image_front: FileList;
    license_image_back: FileList;
}

interface BranchOption {
    id: number;
    branch_name: string;
}

const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

export default function AddDriver() {
    const navigate = useNavigate();
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);
    const [branches, setBranches] = useState<BranchOption[]>([]);

    const {
        handleSubmit,
        control,
        reset,
        formState: { errors },
    } = useForm<FormValues>();

    React.useEffect(() => {
        const fetchBranches = async () => {
            try {
                const branchData: any = await apiPost("branches/list", { status: 'Active' });
                setBranches(branchData);
            } catch (error) {
                console.error("Error fetching branches:", error);
                toast.error("Failed to fetch branches");
            }
        };
        fetchBranches();
    }, []);

    const validatePhoneNumber = (value: string) => {
        if (!value) {
            return "Phone number is required.";
        }
        const sanitizedValue = value.replace(/\D/g, "");
        const phoneNumber = parsePhoneNumberFromString(`+${sanitizedValue}`);
        if (!phoneNumber || !phoneNumber.isValid()) {
            return "Invalid phone number.";
        }
        const allowedCountries: CountryCode[] = ["IN", "SA", "AE"];
        if (!allowedCountries.includes(phoneNumber.country as CountryCode)) {
            return "Only Indian, Saudi Arabian, or UAE numbers are allowed.";
        }
        return true;
    };

    const onSubmit = async (data: FormValues) => {
        setIsButtonDisabled(true);
        try {
            let driverImageName = "";
            let licenseFrontName = "";
            let licenseBackName = "";

            if (data.driver_image && data.driver_image.length > 0) {
                driverImageName = await uploadFile(data.driver_image[0]);
            }
            if (data.license_image_front && data.license_image_front.length > 0) {
                licenseFrontName = await uploadFile(data.license_image_front[0]);
            }
            if (data.license_image_back && data.license_image_back.length > 0) {
                licenseBackName = await uploadFile(data.license_image_back[0]);
            }

            const formData = new FormData();
            formData.append("name", data.name);
            formData.append("email", data.email);
            formData.append("phone_number", data.phone_number);
            formData.append("status", data.status);
            formData.append("gender", data.gender);
            formData.append("date_of_birth", data.date_of_birth);
            if (data.blood_group) formData.append("blood_group", data.blood_group);
            formData.append("address", data.address);
            formData.append("nationality", data.nationality);
            formData.append("license_number", data.license_number);
            formData.append("license_expiry_date", data.license_expiry_date);
            formData.append("joining_date", data.joining_date);
            formData.append("years_of_experience", data.years_of_experience);
            formData.append("assigned_branch_id", data.assigned_branch_id);

            if (driverImageName) formData.append("driver_image", driverImageName);
            if (licenseFrontName) formData.append("license_image_front", licenseFrontName);
            if (licenseBackName) formData.append("license_image_back", licenseBackName);

            await apiPost("drivers/store", formData);

            toast.success("Driver added successfully", { autoClose: 1000 });
            reset();
            setTimeout(() => {
                navigate(`${process.env.PUBLIC_URL}/driver`);
            }, 1000);

        } catch (err: any) {
            if (err.response && err.response.data && err.response.data.errors) {
                Object.values(err.response.data.errors).forEach((error: any) => {
                    toast.error(error[0] || "Validation Error", { autoClose: 3000 });
                });
            } else {
                const errorMessage = err.response?.data?.message || "An unexpected error occurred";
                toast.error(errorMessage, { autoClose: 1000 });
            }
        } finally {
            setIsButtonDisabled(false);
        }
    };

    const downloadSampleExcel = () => {
        const headers = ["Name", "Email", "Phone Number", "Status", "Gender", "Date of Birth", "Blood Group", "Address", "Nationality", "License Number", "License Expiry Date", "Joining Date", "Years of Experience"];
        const rows = [
            ["John Doe", "john@example.com", "+919876543210", "Active", "Male", "1990-01-01", "O+", "123 Driver St", "Indian", "DL1234567890", "2030-01-01", "2023-01-01", "5"],
            ["Jane Doe", "jane@example.com", "+919876543211", "Inactive", "Female", "1992-05-05", "B+", "456 Driver Ln", "Indian", "DL0987654321", "2032-05-05", "2023-02-01", "3"]
        ];

        const worksheet = (XLSX as any).utils.aoa_to_sheet([headers, ...rows]);
        const workbook = (XLSX as any).utils.book_new();
        (XLSX as any).utils.book_append_sheet(workbook, worksheet, "Drivers");
        (XLSX as any).writeFile(workbook, "driver_import_sample.xlsx");
    };

    const [isUploading, setIsUploading] = useState(false);

    const handleBulkImport = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setIsUploading(true);
            const file = e.target.files[0];

            const reader = new FileReader();
            reader.onload = async (evt) => {
                const arrayBuffer = evt.target?.result;
                const wb = (XLSX as any).read(arrayBuffer, { type: 'array' });
                const wsname = wb.SheetNames[0];
                const ws = wb.Sheets[wsname];
                const data: any[][] = (XLSX as any).utils.sheet_to_json(ws, { header: 1, raw: false });

                const validationErrors: string[] = [];
                if (data.length <= 1) {
                    toast.error("File appears to be empty or contains only headers.");
                    e.target.value = '';
                    setIsUploading(false);
                    return;
                }

                // Loop rows (skip header)
                for (let i = 1; i < data.length; i++) {
                    const row = data[i];
                    if (!row || row.length === 0) continue;

                    // Helper to check required
                    const checkRequired = (val: any) => val !== undefined && val !== null && val.toString().trim() !== "";

                    if (!checkRequired(row[0])) validationErrors.push(`Row ${i + 1}: Name is required`);
                    if (!checkRequired(row[1])) validationErrors.push(`Row ${i + 1}: Email is required`);
                    if (!checkRequired(row[2])) validationErrors.push(`Row ${i + 1}: Phone Number is required`);

                    if (!checkRequired(row[3])) validationErrors.push(`Row ${i + 1}: Status is required`);
                    else if (!['Active', 'Inactive'].includes(row[3].toString().trim())) validationErrors.push(`Row ${i + 1}: Status must be 'Active' or 'Inactive'`);

                    if (!checkRequired(row[4])) validationErrors.push(`Row ${i + 1}: Gender is required`);
                    else if (!['Male', 'Female', 'Other'].includes(row[4].toString().trim())) validationErrors.push(`Row ${i + 1}: Gender must be 'Male', 'Female', or 'Other'`);

                    if (!checkRequired(row[5])) validationErrors.push(`Row ${i + 1}: DOB is required`);
                    // Relax date check to allow just presence check if raw string fails regex? No, keep format strict but maybe help user.
                    else if (!/^\d{4}-\d{2}-\d{2}$/.test(row[5].toString().trim())) validationErrors.push(`Row ${i + 1}: DOB must be YYYY-MM-DD (Got: ${row[5]})`);

                    if (!checkRequired(row[6])) validationErrors.push(`Row ${i + 1}: Blood Group is required`);
                    else if (!['A+', 'A-', 'B+', 'B-', 'O+', 'O-', 'AB+', 'AB-'].includes(row[6].toString().trim())) validationErrors.push(`Row ${i + 1}: Blood Group must be A+, A-, B+, B-, O+, O-, AB+, or AB-`);

                    if (!checkRequired(row[7])) validationErrors.push(`Row ${i + 1}: Address is required`);
                    if (!checkRequired(row[8])) validationErrors.push(`Row ${i + 1}: Nationality is required`);
                    if (!checkRequired(row[9])) validationErrors.push(`Row ${i + 1}: License Number is required`);

                    if (!checkRequired(row[10])) validationErrors.push(`Row ${i + 1}: License Expiry is required`);
                    else if (!/^\d{4}-\d{2}-\d{2}$/.test(row[10].toString().trim())) validationErrors.push(`Row ${i + 1}: License Expiry must be YYYY-MM-DD`);

                    if (!checkRequired(row[11])) validationErrors.push(`Row ${i + 1}: Joining Date is required`);
                    else if (!/^\d{4}-\d{2}-\d{2}$/.test(row[11].toString().trim())) validationErrors.push(`Row ${i + 1}: Joining Date must be YYYY-MM-DD`);

                    if (!checkRequired(row[12])) validationErrors.push(`Row ${i + 1}: Experience is required`);
                }

                if (validationErrors.length > 0) {
                    toast.error("Missing fields detected in the Excel file. Please check your data.");
                    e.target.value = '';
                    setIsUploading(false);
                    return;
                }

                const formData = new FormData();
                formData.append('file', file);

                try {
                    const response: any = await apiPost("drivers/import", formData);
                    if (response.errors && response.errors.length > 0) {
                        response.errors.slice(0, 5).forEach((err: string) => toast.error(err));
                        if (response.errors.length > 5) toast.warning(`Check console for ${response.errors.length - 5} more errors.`);
                    } else {
                        toast.success(response.message);
                    }
                    navigate(`${process.env.PUBLIC_URL}/driver`);
                } catch (error: any) {
                    const errMsg = error.response?.data?.message || error.message || "Import failed";
                    toast.error(errMsg);
                    if (error.response?.data?.error) {
                        toast.error(error.response.data.error);
                    }
                } finally {
                    e.target.value = '';
                    setIsUploading(false);
                }
            };
            reader.readAsArrayBuffer(file);
        }
    };

    return (
        <div className='page-body'>
            <Breadcrumbs pageTitle="Add Driver" parent="General" title="Add Driver" />
            <Container fluid>
                <Col xl={12} className="mb-lg-3">
                    <Card>
                        <CardBody className="custom-input form-validation">
                            <Row className="mb-4">
                                <Col md={8}>
                                    <div className="mb-3">
                                        <h5>Import Instructions:</h5>
                                        <ul className="list-group list-group-flush small">
                                            <li className="list-group-item py-1">1. Your Excel data should be in the format: <strong>Name, Email, Phone, Status, Gender, DOB(YYYY-MM-DD), Blood Group, Address, Nationality, License No, Expiry(YYYY-MM-DD), Joining(YYYY-MM-DD), Experience</strong>.</li>
                                            <li className="list-group-item py-1">2. <strong>Status</strong>: Active or Inactive. | <strong>Gender</strong>: Male, Female, or Other.</li>
                                            <li className="list-group-item py-1">3. <strong>Dates</strong> must be purely YYYY-MM-DD format (e.g., 2025-01-30).</li>
                                            <li className="list-group-item py-1">4. <strong>Blood Group</strong>: A+, A-, B+, B-, O+, O-, AB+, AB-.</li>
                                            <li className="list-group-item py-1">5. Phone numbers must include a valid country code. Only India (+91), UAE (+971), and Saudi Arabia (+966) numbers are allowed (e.g., +919876543210, +971516144983, +966597058094).</li>
                                        </ul>
                                    </div>
                                </Col>
                                <Col md={4} className="d-flex align-items-center justify-content-end gap-2">
                                    <Btn color="success" size="sm" onClick={downloadSampleExcel} outline>
                                        Download Sample Excel
                                    </Btn>
                                    <div style={{ position: 'relative', overflow: 'hidden', display: 'inline-block' }}>
                                        <Btn color="info" size="sm" outline disabled={isUploading}>
                                            {isUploading ? "Uploading..." : "Upload Excel"}
                                        </Btn>
                                        <input
                                            type="file"
                                            accept=".xlsx, .xls"
                                            onChange={handleBulkImport}
                                            disabled={isUploading}
                                            style={{
                                                position: 'absolute',
                                                left: 0,
                                                top: 0,
                                                opacity: 0,
                                                width: '100%',
                                                height: '100%',
                                                cursor: isUploading ? 'not-allowed' : 'pointer'
                                            }}
                                        />
                                    </div>
                                </Col>
                            </Row>
                            <Form onSubmit={handleSubmit(onSubmit)} noValidate>
                                <Row className="g-3">
                                    <Col md={4}>
                                        <Label>Name <span className="text-danger">*</span></Label>
                                        <Controller
                                            name="name"
                                            control={control}
                                            rules={{ required: "Name is required" }}
                                            render={({ field }) => (
                                                <>
                                                    <Input {...field} placeholder="Enter Name" invalid={!!errors.name} style={{ borderColor: errors.name ? "red" : "" }} autoComplete="off" />
                                                    <FormFeedback>{errors.name?.message}</FormFeedback>
                                                </>
                                            )}
                                        />
                                    </Col>
                                    <Col md={4}>
                                        <Label>Email <span className="text-danger">*</span></Label>
                                        <Controller
                                            name="email"
                                            control={control}
                                            rules={{
                                                required: "Email is required",
                                                pattern: {
                                                    value: /^[a-zA-Z0-9]+([._%+-][a-zA-Z0-9]+)*@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,63}$/,
                                                    message: "Invalid email"
                                                }
                                            }}
                                            render={({ field }) => (
                                                <>
                                                    <Input type="email" {...field} placeholder="Enter Email" invalid={!!errors.email} style={{ borderColor: errors.email ? "red" : "" }} autoComplete="off" />
                                                    <FormFeedback>{errors.email?.message}</FormFeedback>
                                                </>
                                            )}
                                        />
                                    </Col>
                                    <Col md={4}>
                                        <Label>Phone Number <span className="text-danger">*</span></Label>
                                        <Controller
                                            name="phone_number"
                                            control={control}
                                            rules={{
                                                required: "Phone number is required",
                                                validate: validatePhoneNumber
                                            }}
                                            render={({ field }) => (
                                                <>
                                                    <PhoneInput
                                                        country={"ae"}
                                                        onlyCountries={["ae", "sa", "in"]}
                                                        preferredCountries={["ae"]}
                                                        value={field.value}
                                                        onChange={(value) => field.onChange(value)}
                                                        inputProps={{
                                                            name: "phone_number",
                                                            placeholder: "Enter Phone",
                                                            autoComplete: "none"
                                                        }}
                                                        inputStyle={{
                                                            width: '100%',
                                                            borderColor: errors.phone_number ? 'red' : '#ced4da'
                                                        }}
                                                    />
                                                    {errors.phone_number && <FormFeedback style={{ display: 'block' }}>{errors.phone_number.message}</FormFeedback>}
                                                </>
                                            )}
                                        />
                                    </Col>

                                    <Col md={4}>
                                        <Label>Status <span className="text-danger">*</span></Label>
                                        <Controller
                                            name="status"
                                            control={control}
                                            defaultValue=""
                                            rules={{ required: "Status is required" }}
                                            render={({ field }) => (
                                                <>
                                                    <Input type="select" {...field} invalid={!!errors.status} style={{ borderColor: errors.status ? "red" : "" }} autoComplete="off">
                                                        <option value="">Select Status</option>
                                                        <option value="Active">Active</option>
                                                        <option value="Inactive">Inactive</option>
                                                    </Input>
                                                    <FormFeedback>{errors.status?.message}</FormFeedback>
                                                </>
                                            )}
                                        />
                                    </Col>
                                    <Col md={4}>
                                        <Label>Gender <span className="text-danger">*</span></Label>
                                        <Controller
                                            name="gender"
                                            control={control}
                                            defaultValue=""
                                            rules={{ required: "Gender is required" }}
                                            render={({ field }) => (
                                                <>
                                                    <Input type="select" {...field} invalid={!!errors.gender} style={{ borderColor: errors.gender ? "red" : "" }} autoComplete="off">
                                                        <option value="">Select Gender</option>
                                                        <option value="Male">Male</option>
                                                        <option value="Female">Female</option>
                                                        <option value="Other">Other</option>
                                                    </Input>
                                                    <FormFeedback>{errors.gender?.message}</FormFeedback>
                                                </>
                                            )}
                                        />
                                    </Col>
                                    <Col md={4}>
                                        <Label>Date of Birth <span className="text-danger">*</span></Label>
                                        <Controller
                                            name="date_of_birth"
                                            control={control}
                                            rules={{ required: "DOB is required" }}
                                            render={({ field }) => (
                                                <>
                                                    <DatePicker
                                                        className={`form-control ${errors.date_of_birth ? "is-invalid" : ""}`}
                                                        selected={field.value ? new Date(field.value) : null}
                                                        onChange={(date) => field.onChange(date ? formatDate(date) : "")}
                                                        placeholderText="Select Date of Birth"
                                                        dateFormat="yyyy-MM-dd"
                                                        maxDate={new Date()}
                                                        peekNextMonth
                                                        showMonthDropdown
                                                        showYearDropdown
                                                        dropdownMode="select"
                                                        autoComplete="off"
                                                    />
                                                    <FormFeedback style={{ display: errors.date_of_birth ? "block" : "none" }}>{errors.date_of_birth?.message}</FormFeedback>
                                                </>
                                            )}
                                        />
                                    </Col>
                                    <Col md={4}>
                                        <Label>Blood Group <span className="text-danger">*</span></Label>
                                        <Controller
                                            name="blood_group"
                                            control={control}
                                            rules={{ required: "Blood Group is required" }}
                                            render={({ field }) => (
                                                <>
                                                    <Input type="select" {...field} invalid={!!errors.blood_group} style={{ borderColor: errors.blood_group ? "red" : "" }} autoComplete="off">
                                                        <option value="">Select Blood Group</option>
                                                        <option value="A+">A+</option>
                                                        <option value="A-">A-</option>
                                                        <option value="B+">B+</option>
                                                        <option value="B-">B-</option>
                                                        <option value="O+">O+</option>
                                                        <option value="O-">O-</option>
                                                        <option value="AB+">AB+</option>
                                                        <option value="AB-">AB-</option>
                                                    </Input>
                                                    <FormFeedback>{errors.blood_group?.message}</FormFeedback>
                                                </>
                                            )}
                                        />
                                    </Col>

                                    <Col md={8}>
                                        <Label>Address <span className="text-danger">*</span></Label>
                                        <Controller
                                            name="address"
                                            control={control}
                                            rules={{ required: "Address is required" }}
                                            render={({ field }) => (
                                                <>
                                                    <Input type="textarea" rows="1" {...field} autoComplete="off" placeholder="Enter Address" invalid={!!errors.address} style={{ borderColor: errors.address ? "red" : "" }} />
                                                    <FormFeedback>{errors.address?.message}</FormFeedback>
                                                </>
                                            )}
                                        />
                                    </Col>
                                    <Col md={4}>
                                        <Label>Nationality <span className="text-danger">*</span></Label>
                                        <Controller
                                            name="nationality"
                                            control={control}
                                            rules={{ required: "Nationality is required" }}
                                            render={({ field }) => (
                                                <>
                                                    <Input {...field} placeholder="Enter Nationality" invalid={!!errors.nationality} style={{ borderColor: errors.nationality ? "red" : "" }} autoComplete="off" />
                                                    <FormFeedback>{errors.nationality?.message}</FormFeedback>
                                                </>
                                            )}
                                        />
                                    </Col>

                                    <Col md={4}>
                                        <Label>License Number <span className="text-danger">*</span></Label>
                                        <Controller
                                            name="license_number"
                                            control={control}
                                            rules={{ required: "License Number is required" }}
                                            render={({ field }) => (
                                                <>
                                                    <Input {...field} placeholder="Enter License No" invalid={!!errors.license_number} style={{ borderColor: errors.license_number ? "red" : "" }} autoComplete="off" />
                                                    <FormFeedback>{errors.license_number?.message}</FormFeedback>
                                                </>
                                            )}
                                        />
                                    </Col>
                                    <Col md={4}>
                                        <Label>License Expiry Date <span className="text-danger">*</span></Label>
                                        <Controller
                                            name="license_expiry_date"
                                            control={control}
                                            rules={{ required: "Expiry Date is required" }}
                                            render={({ field }) => (
                                                <>
                                                    <DatePicker
                                                        className={`form-control ${errors.license_expiry_date ? "is-invalid" : ""}`}
                                                        selected={field.value ? new Date(field.value) : null}
                                                        onChange={(date) => field.onChange(date ? formatDate(date) : "")}
                                                        placeholderText="Select Expiry Date"
                                                        dateFormat="yyyy-MM-dd"
                                                        // minDate={new Date()}
                                                        peekNextMonth
                                                        showMonthDropdown
                                                        showYearDropdown
                                                        dropdownMode="select"
                                                        autoComplete="off"
                                                    />
                                                    <FormFeedback style={{ display: errors.license_expiry_date ? "block" : "none" }}>{errors.license_expiry_date?.message}</FormFeedback>
                                                </>
                                            )}
                                        />
                                    </Col>
                                    <Col md={4}>
                                        <Label>Joining Date <span className="text-danger">*</span></Label>
                                        <Controller
                                            name="joining_date"
                                            control={control}
                                            rules={{ required: "Joining Date is required" }}
                                            render={({ field }) => (
                                                <>
                                                    <DatePicker
                                                        className={`form-control ${errors.joining_date ? "is-invalid" : ""}`}
                                                        selected={field.value ? new Date(field.value) : null}
                                                        onChange={(date) => field.onChange(date ? formatDate(date) : "")}
                                                        placeholderText="Select Joining Date"
                                                        dateFormat="yyyy-MM-dd"
                                                        maxDate={new Date()}
                                                        peekNextMonth
                                                        showMonthDropdown
                                                        showYearDropdown
                                                        dropdownMode="select"
                                                        autoComplete="off"
                                                    />
                                                    <FormFeedback style={{ display: errors.joining_date ? "block" : "none" }}>{errors.joining_date?.message}</FormFeedback>
                                                </>
                                            )}
                                        />
                                    </Col>

                                    <Col md={4}>
                                        <Label>Years of Experience <span className="text-danger">*</span></Label>
                                        <Controller
                                            name="years_of_experience"
                                            control={control}
                                            rules={{ required: "Experience is required" }}
                                            render={({ field }) => (
                                                <>
                                                    <Input type="number" {...field} placeholder="Enter Experience (Years)" invalid={!!errors.years_of_experience} style={{ borderColor: errors.years_of_experience ? "red" : "" }} autoComplete="off" />
                                                    <FormFeedback>{errors.years_of_experience?.message}</FormFeedback>
                                                </>
                                            )}
                                        />
                                    </Col>

                                    <Col md={4}>
                                        <Label>Assigned Branch <span className="text-danger">*</span></Label>
                                        <Controller
                                            name="assigned_branch_id"
                                            control={control}
                                            defaultValue=""
                                            rules={{ required: "Branch is required" }}
                                            render={({ field }) => (
                                                <>
                                                    <Input type="select" {...field} invalid={!!errors.assigned_branch_id} style={{ borderColor: errors.assigned_branch_id ? "red" : "" }} autoComplete="off">
                                                        <option value="">Select Branch</option>
                                                        {branches.map(branch => (
                                                            <option key={branch.id} value={branch.id}>{branch.branch_name}</option>
                                                        ))}
                                                    </Input>
                                                    <FormFeedback>{errors.assigned_branch_id?.message}</FormFeedback>
                                                </>
                                            )}
                                        />
                                    </Col>
                                    {/* Removed Assigned Vehicle ID (Optional) */}

                                    <Col md={4}>
                                        <Label>Driver Image</Label>
                                        <Controller
                                            name="driver_image"
                                            control={control}
                                            rules={{
                                                validate: (files: any) => {
                                                    if (files && files[0] && files[0].size > 2 * 1024 * 1024) {
                                                        return "File size must be less than 2MB";
                                                    }
                                                    return true;
                                                }
                                            }}
                                            render={({ field: { value, onChange, ...field } }) => (
                                                <>
                                                    <Input
                                                        type="file"
                                                        {...field}
                                                        onChange={(e) => onChange(e.target.files)}
                                                        accept="image/*"
                                                        invalid={!!errors.driver_image}
                                                        style={{ borderColor: errors.driver_image ? "red" : "" }}
                                                    />
                                                    <FormFeedback>{errors.driver_image?.message}</FormFeedback>
                                                </>
                                            )}
                                        />
                                    </Col>
                                    <Col md={4}>
                                        <Label>License Front Image <span className="text-danger">*</span></Label>
                                        <Controller
                                            name="license_image_front"
                                            control={control}
                                            rules={{
                                                required: "Front Image is required",
                                                validate: (files: any) => {
                                                    if (files && files[0] && files[0].size > 2 * 1024 * 1024) {
                                                        return "File size must be less than 2MB";
                                                    }
                                                    return true;
                                                }
                                            }}
                                            render={({ field: { value, onChange, ...field } }) => (
                                                <>
                                                    <Input
                                                        type="file"
                                                        {...field}
                                                        onChange={(e) => onChange(e.target.files)}
                                                        accept="image/*"
                                                        invalid={!!errors.license_image_front}
                                                        style={{ borderColor: errors.license_image_front ? "red" : "" }}
                                                    />
                                                    <FormFeedback>{errors.license_image_front?.message}</FormFeedback>
                                                </>
                                            )}
                                        />
                                    </Col>
                                    <Col md={4}>
                                        <Label>License Back Image <span className="text-danger">*</span></Label>
                                        <Controller
                                            name="license_image_back"
                                            control={control}
                                            rules={{
                                                required: "Back Image is required",
                                                validate: (files: any) => {
                                                    if (files && files[0] && files[0].size > 2 * 1024 * 1024) {
                                                        return "File size must be less than 2MB";
                                                    }
                                                    return true;
                                                }
                                            }}
                                            render={({ field: { value, onChange, ...field } }) => (
                                                <>
                                                    <Input
                                                        type="file"
                                                        {...field}
                                                        onChange={(e) => onChange(e.target.files)}
                                                        accept="image/*"
                                                        invalid={!!errors.license_image_back}
                                                        style={{ borderColor: errors.license_image_back ? "red" : "" }}
                                                    />
                                                    <FormFeedback>{errors.license_image_back?.message}</FormFeedback>
                                                </>
                                            )}
                                        />
                                    </Col>

                                    <Col xs={12} className="mt-2">
                                        <Btn color="primary" disabled={isButtonDisabled}>
                                            {isButtonDisabled ? <>Submiting <Spinner size="sm" /></> : "Submit"}
                                        </Btn>
                                    </Col>
                                </Row>
                            </Form>
                        </CardBody>
                    </Card>
                </Col>
            </Container>
            <ToastContainer autoClose={1000} />
        </div>
    );
}
